import {
  FC,
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";
import Icon from "../../../../components/Icon/Icon";
import {
  DeliveryOrderStatusE,
  FileTypeDescFromNumE,
  FileTypeE,
  FileTypeHebrewE,
  IconColorE,
  LangE,
  ProjectFileStatusE,
  StatusE,
  TooltipPlaceE,
  UserRoleE,
  VideoContentStatusE,
  VideoContentStatusEnE,
} from "../../../../models/enums";
import { useCopyText } from "../../../../utils/helpers/copyText";
import usePopup from "../../../../utils/hooks/usePopup";
import VideoPlayer from "../../../../popups/VideoPlayer/VideoPlayer";
import { isMobileOrTablet } from "../../../../utils/helpers/mediaQueries";
import MobileVideoPreview from "../../../../components/MobileVideoPreview/MobileVideoPreview";
import { EnumDescriptionItemListT, VersionT } from "../../../../models/models";
import dayjs from "dayjs";
import {
  useDeleteUploadedFileMutation,
  useLazyGetProjectByIdQuery,
} from "../../../../api/projectsApi";
import { clss } from "../../../../utils/helpers/styleHelpers";
import VideoStatusIcon from "../VideoStatusIcon/VideoStatusIcon";
import { hasVideoExtraFrames } from "../../../../utils/helpers/hasVideoExtraFrames";
import VideoControlButtons from "../VideoControlButtons/VideoControlButtons";
import Tooltip from "../../../../components/Tooltip/Tooltip";
import { useTranslate } from "../../../../utils/hooks/useTranslate";
import { useAppSelector } from "../../../../store/hooks";
import {
  selectCurrentLang,
  selectDirection,
} from "../../../../store/selectors/appSelectors";
import { useLocation } from "react-router-dom";
import {
  COMMUNITY_TIME_OLD_VALUE,
  GRANTED_FPS,
  HD_HEIGHT,
  HD_WIDTH,
  UNCLEAR_HOUSE_NUMBER_VALUE,
} from "../../../../utils/constants";
import { hasVideoGrantedExtraFrames } from "../../../../utils/helpers/hasVideoGrantedExtraFrames";
import calcFrameRate from "../../../../utils/helpers/calcFrameRate";
import AccessRights from "../../../../components/AccessRights/AccessRights";
import ReprocessIcon from "../../../../components/ReprocessIcon/ReprocessIcon";
import { useDragUploader } from "../../../../utils/hooks/useDragUploader";
import { selectCurrentUser } from "../../../../store/selectors/authSelectors";
import { Span } from "../../../../components/Text/Text";
import canDragAndDropVersion from "../../../../utils/helpers/canDragAndDropVersion";
import styles from "./videoContentRow.module.scss";
import { BroadcastersOverlayPlayerQueryI } from "../../ViewVideoProject/models";

type VideoContentRowPropTypes = {
  projectId: string;
  projectStatus: StatusE;
  item: VersionT;
  fileTypes?: EnumDescriptionItemListT[];
  broadcastingDate: Date | string;
  refetchProject: () => void;
  index: number;
  editable?: boolean;
  broadcastersOverlayPlayerData?: BroadcastersOverlayPlayerQueryI;
  isFetchingData: boolean;
  // spec-37 — passed straight through to VideoControlButtons, which needs it to decide whether the
  // "Remove version" bin is offered to a delivery-service user at
  // WaitingForApprovalOfAdvertisingAgency. Optional: the campaign page (VersionList) renders this row
  // without a project payload and has no order to give.
  orderStatus?: DeliveryOrderStatusE | null;
};

const VideoContentRow: FC<VideoContentRowPropTypes> = ({
  item,
  broadcastingDate,
  refetchProject,
  projectId,
  projectStatus,
  index,
  editable = true,
  broadcastersOverlayPlayerData,
  isFetchingData = false,
  orderStatus,
}) => {
  // Use ref to always have current value
  const broadcastersOverlayPlayerDataRef = useRef(broadcastersOverlayPlayerData);
  broadcastersOverlayPlayerDataRef.current = broadcastersOverlayPlayerData;

  const user = useAppSelector(selectCurrentUser);

  const { t } = useTranslate();
  const lang = useAppSelector(selectCurrentLang);
  const dir = useAppSelector(selectDirection);
  const {
    StatusName,
    AdId,
    FileType,
    RequiredLength,
    ActualLength,
    fileFormat,
    EncodingErrorDescription,
    UploadErrorDescription,
    HouseNumber,
    sponsershipHouseNumber,
    Name,
    useFor2Spots,
    VideoHeight,
    VideoWidth,
    NotSuitableForBroadcast,
    NotSuitableForBroadcastComment,
    NotSuitableForBroadcastReason,
    NotSuitableForBroadcastType,
    ActualFrameCount,
    UploadDateTime,
    Id,
    SecondHouseNumberType,
    DesktopPlaybackUrl,
    isChannel5or9,
  } = item;
  const { createPopup } = usePopup();
  const [showMobilePlayer, setShowMobilePlayer] = useState(false);
  const [deleteUploadedFile, { isLoading: isDeleting }] =
    useDeleteUploadedFileMutation();
  const [getProject, { isFetching: isFetchingProject }] =
    useLazyGetProjectByIdQuery();
  const [isPreUploaded, setIsPreUploaded] = useState(false);
  const location = useLocation();
  const isCampaignPage = location.pathname.includes("campaign");
  const { handleDrag, handleDrop } = useDragUploader();
  const copyText = useCopyText();

  const isHDResolution =
    VideoHeight && VideoWidth
      ? +VideoWidth === HD_WIDTH && +VideoHeight === HD_HEIGHT
      : true;
  const isUploading =
    StatusName === VideoContentStatusE.UPLOADING ||
    StatusName === VideoContentStatusE.ENCODING ||
    StatusName === VideoContentStatusE.UPLOADED;
  const isShowStopperError =
    isUploading || StatusName === VideoContentStatusE.WAITING
      ? false
      : (!isHDResolution ||
        !!UploadErrorDescription ||
        NotSuitableForBroadcast ||
        StatusName === VideoContentStatusE.UPLOADED_ERROR ||
        ActualLength === 0 ||
        (ActualLength &&
          RequiredLength &&
          !hasVideoGrantedExtraFrames(ActualFrameCount, RequiredLength)) ||
        (ActualLength &&
          RequiredLength &&
          Math.trunc(ActualLength) !== Math.trunc(RequiredLength))) &&
      !isDeleting &&
      !isFetchingProject;

  const onFileAdded = useCallback((e) => {
    if (e.detail.adId === AdId) {
      setIsPreUploaded(true);
    }
  }, []);

  const onFileRemoved = useCallback((e) => {
    if (e.detail.adId === AdId) {
      setIsPreUploaded(false);
    }
  }, []);

  useEffect(() => {
    if (StatusName !== VideoContentStatusE.WAITING) {
      // update (reset) IsPreUpload State if file uploaded
      setIsPreUploaded(false);
    }
  }, [StatusName]);

  useEffect(() => {
    if (StatusName === VideoContentStatusE.UPLOADING) {
      deleteUploadedFile({ fileId: Id });
    }

    document.addEventListener("file-added", onFileAdded);
    document.addEventListener("file-removed", onFileRemoved);
    return () => {
      document.removeEventListener("file-added", onFileAdded);
      document.removeEventListener("file-removed", onFileRemoved);
    };
  }, []);

  const toggleShowMobilePlayer = () => {
    setShowMobilePlayer((prev) => !prev);
  };

  const backgroundStyle = useMemo(() => {
    if (isUploading) {
      return styles.skeletonWrapper;
    }
    if (StatusName === VideoContentStatusE.WAITING) {
      // gray background
      return isPreUploaded ? styles.darkGrayWrapper : styles.grayWrapper;
    } else if (isShowStopperError) {
      // red background
      return styles.redWrapper;
    } else {
      // green background
      return styles.greenWrapper;
    }
  }, [StatusName, isPreUploaded, isShowStopperError, isUploading]);

  const openVideoPlayer = useCallback(() => {
    createPopup({
      body: <VideoPlayer item={item} broadcastersOverlayPlayerData={broadcastersOverlayPlayerDataRef.current} />,
      className: "video-player-popup",
    });
  }, [item, createPopup]);

  const VideoId = ({ houseNumber, isDuplicate }) => (
    <div className={styles.videoIdWrapper}>
      <div className={styles.idWrapper}>
        {!isDuplicate && editable && (
          <Icon
            name="copy"
            color={IconColorE.BLUE}
            className={clss([styles[`copyIcon${dir}`], styles.copyIcon])}
            onClick={() => copyText(AdId)}
            title="Copy"
          />
        )}
        <span>{isDuplicate ? t`Displayed for duplicate inlay` : AdId}</span>
      </div>
      {houseNumber && houseNumber !== UNCLEAR_HOUSE_NUMBER_VALUE && (
        <span className={styles.houseNumber}>(HN#: {houseNumber})</span>
      )}
    </div>
  );

  const extraFramesCount = useMemo(
    () => hasVideoExtraFrames(ActualFrameCount, ActualLength),
    [RequiredLength, ActualLength]
  );
  const extraFramesString = useMemo(
    () => (extraFramesCount ? `+${extraFramesCount}` : ""),
    [extraFramesCount]
  );
  const formattedRequiredLength = useMemo(() => {
    const formatted = dayjs(RequiredLength * 1000).format("mm:ss.SSS");
    const millisecondsFormatted = formatted.split(".")[1];
    if (+millisecondsFormatted === 0) return formatted.slice(0, -4);
    return formatted;
  }, [RequiredLength]);
  const formattedActualLength = useMemo(() => {
    const formatted = dayjs(ActualLength * 1000).format("mm:ss.SSS");
    const millisecondsFormatted = formatted.split(".")[1];
    if (+millisecondsFormatted === 0) return formatted.slice(0, -4);
    return formatted;
  }, [ActualLength]);
  const frameRate = useMemo(
    () =>
      ActualFrameCount && ActualLength
        ? calcFrameRate(ActualFrameCount, ActualLength)
        : 0,
    [ActualFrameCount, ActualLength]
  );

  const extraFramesErrorText =
    (extraFramesCount
      ? t`Video has extra frame, length doesn't match required one. Check it out!`
      : "") +
    "<br/>" +
    t`Expected length` +
    `: ${formattedRequiredLength}` +
    "<br/>" +
    t`Actual length` +
    (extraFramesCount
      ? `: ${formattedActualLength} + ${extraFramesCount} ` + t`frames`
      : `: ${formattedActualLength}`);

  const VideoLength = () => (
    <div className={styles.videoLengthWrapper}>
      <Tooltip
        withoutTranslation
        text={!UploadErrorDescription && extraFramesErrorText}
      >
        {formattedRequiredLength + " "}
      </Tooltip>
      {/* if video has extra frames - display error. else - display actual length if exists */}
      {!UploadErrorDescription && (
        <>
          {!extraFramesCount ? (
            <Tooltip text={errorText || ""}>
              {/* {ActualLength ? `(${formattedActualLength})` : '(N/A)'} */}
              {`(${formattedActualLength})`}
            </Tooltip>
          ) : (
            <span className={styles.extraFrames}>
              <Tooltip text={extraFramesErrorText} withoutTranslation>
                ({formattedActualLength}
                {extraFramesString})
              </Tooltip>
            </span>
          )}
        </>
      )}
    </div>
  );

  const errorText = (() => {
    if (UploadErrorDescription)
      return (
        (StatusName ? t(VideoContentStatusEnE[StatusName]) : t`Error`) +
        UploadErrorDescription
      );
    if (RequiredLength !== ActualLength)
      return t`Length is not equal to required length.<br />Actual length - ${formattedActualLength}, required - ${formattedRequiredLength}.`;
    if (!isHDResolution)
      return `${t`Required resolution: width - 1920, height - 1080.`} <br />${t`Actual resolution: width`} - ${VideoWidth}, ${t`height`} - ${VideoHeight}.`;
    if (frameRate && frameRate !== GRANTED_FPS)
      return `Encoding Error${UploadErrorDescription}`;
    if (ActualLength && RequiredLength && ActualLength !== RequiredLength) {
      return extraFramesErrorText;
    }

    return "";
  })();

  const notSuitableErrorText = useMemo(() => {
    if (NotSuitableForBroadcast) {
      const comment = NotSuitableForBroadcastComment.split("|")[0];
      const rejecter = NotSuitableForBroadcastComment.split("|")[1];
      const reason =
        NotSuitableForBroadcastReason === 0 ? t`technical reason` : t`other`;
      const type = NotSuitableForBroadcastType === 0 ? t`private` : t`public`;

      return `${t`Rejected by`}: ${rejecter}. ${comment}. ${t`This is`} ${type} ${t`message`}. ${t`The reason of rejection is`} ${reason}.`;
    } else return "";
  }, [
    NotSuitableForBroadcast,
    NotSuitableForBroadcastComment,
    NotSuitableForBroadcastReason,
    NotSuitableForBroadcastType,
    t,
  ]);

  const videoControlButtonsProps = {
    version: item,
    isDeleting,
    isFetchingProject,
    projectStatus,
    broadcastingDate,
    refetchProject,
    deleteUploadedFile,
    getProject,
    projectId,
    isPreUploaded,
    isChannel5or9: item.isChannel5or9,
    orderStatus,
  };

  const dragAndDropProps = canDragAndDropVersion(
    projectStatus,
    user?.role,
    item.isChannel5or9,
    item.fileFormat
  )
    ? {
      onDragOver: (e) => !isPreUploaded && handleDrag(e, AdId),
      onDragLeave: (e) => !isPreUploaded && handleDrag(e, AdId),
      onDrop: (e) => !isPreUploaded && handleDrop(e, AdId, false, false),
    }
    : {};

  return !isMobileOrTablet() ? (
    <div className={styles.wrapperWithTooltip} {...dragAndDropProps}>
      {/* <Tooltip text={isShowStopperError ? errorText : ''} withoutTranslation addTooltipClassName={styles.tooltipBackground}>
                <div className={styles.tooltipRowWrapper} {...dragAndDropProps} />
            </Tooltip> */}
      <div className={clss([styles.rowWrapper])}>
        <AccessRights accessFor={UserRoleE.GlobalAdmin}>
          <div className={styles.reprocessIconWrapper}>
            <ReprocessIcon
              projectFileId={item.Id}
              projectStatus={projectStatus}
            />
          </div>
        </AccessRights>
        <div
          className={clss([
            styles.wrapper,
            isDeleting || isFetchingProject ? styles.disabledWrapper : "",
            editable ? styles.editableWrapper : "",
            backgroundStyle,
          ])}
          {...dragAndDropProps}
          id={AdId}
        >
          <span className={styles.index}>{index + 1}</span>
          <div className={styles.videoContentName}>
            <VideoStatusIcon
              status={
                (item?.StatusName as VideoContentStatusE) ||
                (Object.entries(ProjectFileStatusE).find(
                  (entry) => +entry?.[0] === item?.Status
                )?.[1] as VideoContentStatusE)
              }
              projectStatus={projectStatus}
              onPlay={openVideoPlayer}
              UploadDateTime={UploadDateTime}
              Id={Id}
              EncodingErrorDescription={EncodingErrorDescription}
              isFetchingData={isFetchingData}
            />
            <span>{Name}</span>
          </div>
          <VideoId houseNumber={HouseNumber} isDuplicate={false} />
          <VideoLength />
          <span>
            {
              (lang === LangE.EN ? FileTypeE : FileTypeHebrewE)[
              FileType === COMMUNITY_TIME_OLD_VALUE ? 5 : FileType
              ]
            }
          </span>
          <span className={styles.fileFormatWrapper}>
            {fileFormat}
            {fileFormat === "MXF" &&
              user?.role === UserRoleE.PostProduction && (
                <Tooltip
                  text="This version is required content adaptation"
                  delayShow={300}
                  place={TooltipPlaceE.LEFT}
                >
                  <span className={styles.infoTooltipWrapper}>
                    <Icon name="info" />
                  </span>
                </Tooltip>
              )}
          </span>
          {editable ? (
            <VideoControlButtons {...videoControlButtonsProps} />
          ) : (
            <div />
          )}
          {((isShowStopperError && !isCampaignPage) ||
            item.NotSuitableForBroadcast) && (
              <div
                className={
                  useFor2Spots
                    ? styles.resolutionAlertDuplicate
                    : styles.resolutionAlert
                }
              >
                <Icon
                  name="exclamation"
                  title={
                    errorText ||
                    notSuitableErrorText ||
                    item.NotSuitableForBroadcastComment
                  }
                  withoutTranslation
                />
              </div>
            )}
        </div>
        {useFor2Spots && (
          <div
            className={clss([
              styles.wrapper,
              styles.duplicateWrapper,
              editable ? styles.editableWrapper : "",
            ])}
          >
            <span className={styles.index}>{index + 1}.1</span>
            <div className={styles.videoContentName} />
            <VideoId houseNumber={sponsershipHouseNumber} isDuplicate />
            <VideoLength />
            <span>
              {
                (lang === LangE.EN ? FileTypeE : FileTypeHebrewE)[
                SecondHouseNumberType || FileTypeE.Sponsorship
                ]
              }
            </span>
            <span>{fileFormat}</span>
            {editable ? (
              <VideoControlButtons
                {...videoControlButtonsProps}
                asSponsorship
                isDuplicate={true}
              />
            ) : (
              <div />
            )}
          </div>
        )}
        {fileFormat === "MP4" && isChannel5or9 && (
          <div
            className={clss([
              styles.wrapper,
              styles.duplicateWrapper,
              editable ? styles.editableWrapper : "",
            ])}
          >
            <span className={styles.index}>
              {index + 1}.{useFor2Spots ? 2 : 1}
            </span>
            <div className={styles.videoContentName} />
            <Span>(Content adaptation version)</Span>
            <VideoLength />
            <span>
              {
                (lang === LangE.EN ? FileTypeE : FileTypeHebrewE)[
                FileType === COMMUNITY_TIME_OLD_VALUE ? 5 : FileType
                ]
              }
            </span>
            <span>MXF</span>
            <div />
          </div>
        )}
      </div>
    </div>
  ) : (
    <div className={clss([styles.rowWrapper])} id="version">
      <AccessRights accessFor={UserRoleE.GlobalAdmin}>
        <ReprocessIcon
          projectFileId={item.Id}
          projectStatus={projectStatus}
          mobile
        />
      </AccessRights>

      <div
        className={clss([
          styles.mobileWrapper,
          isDeleting || isFetchingProject ? styles.disabledWrapper : "",
          backgroundStyle,
        ])}
      >
        <div className={styles.infoWrapper}>
          {((isShowStopperError && !isCampaignPage) ||
            item.NotSuitableForBroadcast) && (
              <div
                className={
                  useFor2Spots
                    ? styles.resolutionAlertDuplicate
                    : styles.resolutionAlert
                }
              >
                <Icon
                  name="exclamation"
                  title={
                    errorText ||
                    notSuitableErrorText ||
                    item.NotSuitableForBroadcastComment
                  }
                  withoutTranslation
                  isShowTooltip={true}
                />
              </div>
            )}
          <div className={styles.videoStatusMobile}>
            <VideoStatusIcon
              status={item?.StatusName as VideoContentStatusE}
              projectStatus={projectStatus}
              onPlay={openVideoPlayer}
              UploadDateTime={UploadDateTime}
              EncodingErrorDescription={EncodingErrorDescription}
              Id={Id}
              isFetchingData={isFetchingData}
            />
            <span>{Name}</span>
          </div>
        </div>

        {editable ? (
          <VideoControlButtons {...videoControlButtonsProps} />
        ) : (
          <div />
        )}
        <VideoId houseNumber={HouseNumber} isDuplicate={false} />
        <div>{fileFormat}</div>
        <div>
          {
            FileTypeDescFromNumE[
            FileType === COMMUNITY_TIME_OLD_VALUE ? 5 : FileType
            ]
          }
        </div>
        <VideoLength />
        {showMobilePlayer && (
          <MobileVideoPreview item={item} onClose={toggleShowMobilePlayer} />
        )}
      </div>

      {useFor2Spots && (
        <div className={clss([styles.mobileWrapper, styles.duplicateWrapper])}>
          {/* <div className={styles.videoStatusMobile} /> */}
          <VideoId houseNumber={sponsershipHouseNumber} isDuplicate={true} />
          <div>{fileFormat}</div>
          <div>
            {
              FileTypeDescFromNumE[
              FileType === COMMUNITY_TIME_OLD_VALUE ? 5 : FileType
              ]
            }
          </div>
          <VideoLength />
          {showMobilePlayer && (
            <MobileVideoPreview item={item} onClose={toggleShowMobilePlayer} />
          )}
        </div>
      )}
    </div>
  );
};

export default VideoContentRow;
